import {updateCloudinarySize} from '../cloudinary'
import type {ProductImages, ProductVideos} from '../../types/product'

export interface MediaItem {
    type: 'image' | 'video'
    url: string
    alt?: string
    isHorizontal?: boolean
    urlPdpZoom?: string
    urlSwatch?: string
}

interface ComputeMediaItemsOptions {
    images?: ProductImages
    videos?: ProductVideos
    isDesktop?: boolean
    isTablet?: boolean
    type?: 'grid3' | 'grid2' | 'pdp' | 'full' | 'horizontal' | 'wishlist' | 'shoptheimage' | 'shopbylook'
}

/**
 * Computes the ordered list of media items for a product variation.
 * Shared between ProductMediaCarousel and ProductDetails so both use
 * identical ordering logic and URL optimization.
 */
export function computeMediaItems({images, videos, isDesktop, isTablet, type}: ComputeMediaItemsOptions): MediaItem[] {
    const items: MediaItem[] = []
    const deviceType = isDesktop ? 'desktop' : isTablet ? 'tablet' : 'mobile'
    const size = type ? (TILE_ASSET_SIZE as any)[deviceType][type] : isDesktop ? window.innerWidth / 2 : window.innerWidth
    images?.large
        ?.filter((img) => Boolean(img?.url))
        .forEach((img) =>
            items.push({
                type: 'image',
                url: updateCloudinarySize(img.url as string, size),
                urlPdpZoom: img.url,
                urlSwatch: updateCloudinarySize(img.url as string, 112),
                alt: img.alt,
                isHorizontal: false,
            }),
        )

    if (images?.horizontal?.url && type !== 'shopbylook') {
        items.push({
            type: 'image',
            url: updateCloudinarySize(images.horizontal.url, size),
            alt: images.horizontal.alt,
            isHorizontal: true,
            urlPdpZoom: images.horizontal.url,
            urlSwatch: updateCloudinarySize(images.horizontal.url, 112),
        })
    }
    const hasHorizontalImage = Boolean(images?.horizontal?.url)

    const videoV = videos?.hasVerticalVideo && type !== 'shopbylook' ? videos.videoV?.CLDvideoVURL : undefined
    const videoH = videos?.hasHorizontalVideo && type !== 'shopbylook' ? videos.videoH?.CLDvideoHURL : undefined

    if (hasHorizontalImage && videoV) items.unshift({type: 'video', url: videoV, isHorizontal: false})
    else if (videoV) items.push({type: 'video', url: videoV, isHorizontal: false})
    if (videoH) items.push({type: 'video', url: videoH, isHorizontal: true})

    return items
}

export const TILE_ASSET_SIZE = {
    mobile: {
        grid3: 300,
        grid2: 500,
        full: 500,
        horizontal: 500,
        wishlist: 500,
        shoptheimage: 300,
        pdp: 500,
        carousel: 500,
        shopbylook: 500,
    },
    tablet: {
        grid3: 320,
        grid2: 490,
        full: 600,
        horizontal: 780,
        wishlist: 490,
        shoptheimage: 320,
        pdp: 1000,
        carousel: 600,
        shopbylook: 1000,
    },
    desktop: {
        grid3: 900,
        grid2: 1000,
        full: 1230,
        horizontal: 1600,
        wishlist: 900,
        shoptheimage: 900,
        pdp: 1400,
        carousel: 900,
        shopbylook: 1400,
    },
}
